// Copyright (c) 2025 by Audere

import { AvatarGroup, List, ListItem, Popover } from "@mui/material";
import React from "react";
import { AnnotatorListItem, StyledBadge } from "./AnnotatorListItem";
import { CustomAvatar } from "./CustomAvatar";
import { LabelerData, QCData } from "../features/api/labelApiSlice";
import { createStyleSheet } from "../utils/styles";
import { useCurrentUser } from "../hooks/useCurrentUser";

interface AnnotatorBadgeProps {
  annotators: (LabelerData | QCData)[];
  group?: string;
}

const AnnotatorBadge: React.FC<AnnotatorBadgeProps> = ({
  annotators,
  group,
}) => {
  const user = useCurrentUser();
  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);

  const handlePopoverOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
  };
  const open = Boolean(anchorEl);

  return (
    <>
      {/* Labeler icons */}
      <AvatarGroup
        max={4}
        sx={styles.avatarGroup}
        onMouseEnter={(e) => {
          handlePopoverOpen(e);
        }}
        onMouseLeave={handlePopoverClose}
        aria-haspopup="true"
        aria-owns={open ? "mouse-over-popover" : undefined}
        aria-label={`Annotators for ${group}`}
      >
        {annotators.map((annotator) => {
          const isUser = annotator.email === user.email;
          return isUser ? (
            <StyledBadge
              key={annotator.email}
              overlap="circular"
              badgeContent="YOU"
              anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
            >
              <CustomAvatar user={annotator} />
            </StyledBadge>
          ) : (
            <CustomAvatar key={annotator.email} user={annotator} />
          );
        })}
      </AvatarGroup>

      {/* Labeler details popover */}
      {annotators.length > 0 && (
        <Popover
          sx={{ pointerEvents: "none" }}
          open={open}
          anchorEl={anchorEl}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "center",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "center",
          }}
          onClose={handlePopoverClose}
          disableRestoreFocus
          aria-label="Annotators for this conversation"
        >
          <List disablePadding>
            {annotators.map((annotator) => (
              <ListItem key={annotator.email}>
                <AnnotatorListItem annotator={annotator} />
              </ListItem>
            ))}
          </List>
        </Popover>
      )}
    </>
  );
};

const styles = createStyleSheet({
  avatarGroup: {
    justifyContent: "center",
    marginTop: 1,
  },
});

export default AnnotatorBadge;
